import styles from '@ui/components/List/List.module.scss';
import Content from '@ui/components/Content/Content';
import React, { Children, cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const List = ({
  align = 'leading',
  children,
  className,
  icon: Icon = null,
  iconSize = 'default',
  isDense = false,
  noSpacing = false,
  type = 'ul',
  variant = 'success',
}) => {
  const Tag = type === 'checkmark' ? 'ul' : type;

  // some icons are circular already, so we don't need to add the '.circle' class
  const isCircularIcon = Icon?.props?.icon?.includes('circle');
  const iconStyle = isCircularIcon ? 'noBackgroundCircle' : 'circle';

  const renderCheckMark = () => {
    if (!Icon) {
      return "The prop 'icon' must be passed when used with the type 'checkmark'";
    }

    return (
      <span
        className={classNames(
          styles[variant],
          styles[iconSize],
          styles[iconStyle],
          { [styles.dense]: isDense },
        )}
      >
        {cloneElement(Icon, {
          className: classNames(
            styles.icon,
            styles[variant],
            styles[iconSize],
            { [styles.circularIcon]: isCircularIcon },
          ),
        })}
      </span>
    );
  };

  const renderBullet = () => {
    if (!Icon) {
      return "The prop 'icon' must be passed when used with the type 'ul'";
    }

    return cloneElement(Icon, { className: styles.icon });
  };

  const getIcon = () => {
    switch (type) {
      case 'checkmark':
        return renderCheckMark();
      case 'ul':
        return renderBullet();
      default:
        return null;
    }
  };

  return (
    <Tag
      className={classNames(
        styles.list,
        styles[iconSize],
        { [styles.noSpacing]: noSpacing, [styles.dense]: isDense },
        className,
      )}
    >
      {Children.toArray(children)
        .filter(child => isValidElement(child))
        .map(child =>
          cloneElement(child, {
            type,
            icon: getIcon(),
            align,
            variant,
            iconSize,
            circularIcon: isCircularIcon,
          }),
        )}
    </Tag>
  );
};

List.propTypes = {
  align: PropTypes.oneOf(['leading', 'center']),
  children: PropTypes.node,
  className: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.oneOf(['small', 'default']),
  isDense: PropTypes.bool,
  noSpacing: PropTypes.bool,
  type: PropTypes.oneOf(['ul', 'ol', 'checkmark']),
  variant: PropTypes.oneOf([
    'success',
    'error',
    'contrast',
    'primary',
    'secondary',
    'gold',
  ]),
};

const ListItem = ({
  align = 'leading',
  children,
  circularIcon,
  className,
  icon,
  iconSize,
  type = 'ul',
  variant = 'success',
  ...props
}) => {
  if (!children) {
    return null;
  }

  return (
    <li
      className={classNames(
        styles.listItem,
        styles[type],
        styles[align],
        styles[variant],
        {
          [styles.circularIcon]: circularIcon,
        },
        className,
      )}
      {...props}
    >
      {icon}
      <Content
        as="span"
        className={classNames(styles.text, styles[iconSize], {
          [styles.circularIcon]: circularIcon,
        })}
        theme={variant === 'contrast' ? 'contrast' : 'default'}
        withParser={!isValidElement(children)}
      >
        {children}
      </Content>
    </li>
  );
};

ListItem.propTypes = {
  align: PropTypes.oneOf(['leading', 'center']),
  children: PropTypes.node,
  circularIcon: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.node,
  iconSize: PropTypes.oneOf(['small', 'default']),
  type: PropTypes.oneOf(['ul', 'ol', 'checkmark']),
  variant: PropTypes.oneOf([
    'success',
    'error',
    'contrast',
    'primary',
    'secondary',
    'gold',
  ]),
};

export default List;
export { ListItem };
